import ProductionStorage from './productionStorage';
import { Platform } from 'react-native';

// TS safety for web-only globals referenced behind Platform guards.
declare const window: any;
declare const localStorage: any;
declare const CustomEvent: any;

export interface Appointment {
  id: string;
  customerName: string;
  customerId?: string;
  phone: string;
  email: string;
  address: string; // Customer address (for business owner view)
  serviceType: string;
  vehicleYear?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  vehicleMileage?: string;
  vehicleVin?: string;
  vehicleLicensePlate?: string;
  /** Drop off the vehicle vs wait on-site (automotive bookings). */
  vehicleHandoff?: 'drop_off' | 'wait';
  vinMethod?: 'typed' | 'photo' | 'skipped';
  carPhotos?: string[];
  photoCount?: number;
  vehicleVinPhoto?: string;
  vehicleType?: string;
  serviceHistory?: string;
  specialInstructions?: string;
  appointmentDate: string;
  appointmentTime: string;
  status:
    | 'pending'
    | 'confirmed'
    | 'completed'
    | 'paid'
    | 'cancelled'
    | 'pending_business_confirmation'
    | 'pending_reschedule'
    | 'business_will_call'
    | 'declined'
    | 'expired';
  workerId?: string;
  workerName?: string;
  actualDuration?: number;
  paymentData?: any;
  salonDetails?: string;
  zipCode?: string;
  createdAt: string;
  isRepeat?: boolean;
  repeatFrequency?: 'weekly' | 'biweekly' | 'monthly' | 'bimonthly';
  businessOwnerId?: string; // ID of the business owner this appointment belongs to
  // Business information (for user view)
  businessName?: string;
  businessAddress?: string;
  businessPhone?: string;
  businessEmail?: string;
  businessHours?: any; // Business hours for display in appointment cards
  /** Multi-location support: which business location this appointment belongs to. */
  locationId?: string;
  queueToken?: number;
  queueStage?: 'booked' | 'checked_in' | 'in_service' | 'completed' | 'no_show';
  checkedInAt?: string;
  serviceStartedAt?: string;
  serviceCompletedAt?: string;
  estimatedDurationMinutes?: number;
  delayMinutes?: number;
  delayReason?: string;
}

export interface QueueInsight {
  appointmentId: string;
  yourNumber: number;
  currentServingNumber: number | null;
  peopleAhead: number;
  estimatedStartAt: string | null;
  estimatedWindowStartAt: string | null;
  estimatedWindowEndAt: string | null;
  confidence: 'high' | 'medium' | 'low';
  lastUpdatedAt: string;
}

class AppointmentManager {
  private readonly APPOINTMENTS_KEY = 'appointments';
  private readonly NOTIFICATIONS_KEY = 'notifications';
  private readonly DEFAULT_SERVICE_DURATION_MINUTES = 40;

  /**
   * Firestore reads can return before a just-written doc is visible. On native, saves may be
   * local-only until a backend path exists. Merge keeps local-only rows so bookings don't vanish.
   */
  private mergeFirestoreAppointmentsWithLocal(
    fromFirestore: Appointment[],
    previousLocal: Appointment[]
  ): Appointment[] {
    const byId = new Map<string, Appointment>();
    for (const a of fromFirestore) {
      if (a?.id) byId.set(a.id, a);
    }
    for (const a of previousLocal) {
      if (a?.id && !byId.has(a.id)) byId.set(a.id, a);
    }
    return Array.from(byId.values());
  }

  private async readLocalAppointmentsSnapshot(): Promise<Appointment[]> {
    try {
      const raw = await ProductionStorage.getItem(this.APPOINTMENTS_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /** Firestore does not allow undefined field values. */
  private removeUndefinedForFirestore(obj: any): any {
    if (obj === null || obj === undefined) {
      return null;
    }
    if (Array.isArray(obj)) {
      return obj.map((item) => this.removeUndefinedForFirestore(item)).filter((item) => item !== undefined);
    }
    if (typeof obj === 'object') {
      const cleaned: any = {};
      Object.keys(obj).forEach((key) => {
        const value = obj[key];
        if (value !== undefined) {
          cleaned[key] = this.removeUndefinedForFirestore(value);
        }
      });
      return cleaned;
    }
    return obj;
  }

  /** Remove appointment doc from top-level `appointments/{id}`. */
  private async deleteAppointmentFromTopLevelFirestore(appointmentId: string): Promise<void> {
    if (Platform.OS === 'web') {
      const { doc, deleteDoc } = await import('firebase/firestore');
      const { getWebDb, initializeFirebase } = await import('../services/unifiedAuthService');
      if (!initializeFirebase()) {
        throw new Error('Firebase is not initialized');
      }
      const db = getWebDb();
      if (!db) {
        throw new Error('Firebase database is unavailable');
      }
      await deleteDoc(doc(db, 'appointments', appointmentId));
      return;
    }
    const { initializeFirebase } = await import('../services/unifiedAuthService');
    if (!initializeFirebase()) {
      throw new Error('Firebase is not initialized');
    }
    const { getApp } = require('@react-native-firebase/app');
    const { getFirestore, doc, deleteDoc } = require('@react-native-firebase/firestore');
    const db = getFirestore(getApp());
    await deleteDoc(doc(db, 'appointments', appointmentId));
  }

  /** Merge appointment fields into top-level `appointments/{id}` (same collection as getAppointments). */
  private async persistAppointmentPatchToTopLevelFirestore(
    appointmentId: string,
    patch: Record<string, unknown>
  ): Promise<void> {
    const cleaned = this.removeUndefinedForFirestore(patch) as Record<string, unknown>;
    if (!cleaned || Object.keys(cleaned).length === 0) return;
    const payload = { ...cleaned, updatedAt: new Date().toISOString() };
    if (Platform.OS === 'web') {
      try {
        const { doc, setDoc } = await import('firebase/firestore');
        const { getWebDb, initializeFirebase } = await import('../services/unifiedAuthService');
        if (!initializeFirebase()) return;
        const db = getWebDb();
        if (!db) return;
        await setDoc(doc(db, 'appointments', appointmentId), payload, { merge: true });
      } catch (e) {
        console.warn('⚠️ AppointmentManager: top-level Firestore merge failed (web):', e);
      }
      return;
    }
    try {
      const { initializeFirebase } = await import('../services/unifiedAuthService');
      if (!initializeFirebase()) return;
      const { getApp } = require('@react-native-firebase/app');
      const { getFirestore, doc, setDoc } = require('@react-native-firebase/firestore');
      const db = getFirestore(getApp());
      await setDoc(doc(db, 'appointments', appointmentId), payload, { merge: true });
    } catch (e) {
      console.warn('⚠️ AppointmentManager: top-level Firestore merge failed (mobile):', e);
    }
  }

  // Save a new appointment
  async saveAppointment(appointment: Omit<Appointment, 'id' | 'createdAt'>): Promise<Appointment> {
    try {
      console.log('🔵 AppointmentManager: saveAppointment called with:', {
        email: appointment.email,
        customerName: appointment.customerName,
        serviceType: appointment.serviceType,
        appointmentDate: appointment.appointmentDate,
        appointmentTime: appointment.appointmentTime
      });

      // Check appointment limit before saving (if email is available to identify user)
      if (appointment.email) {
        try {
          const { appointmentLimitService } = await import('../services/appointmentLimitService');
          const { AuthManager } = await import('./authManager');
          const user = await AuthManager.getSavedUser();
          
          // Only check limit for regular users (customers), not business owners
          if (user && user.userType === 'customer') {
            const userId = user.id || appointment.email;
            const limitCheck = await appointmentLimitService.checkAppointmentLimit(userId);
            
            if (!limitCheck.canBook) {
              console.warn('❌ Appointment limit reached:', limitCheck);
              const error: any = new Error('APPOINTMENT_LIMIT_REACHED');
              error.limitCheck = limitCheck;
              throw error;
            }
            
            console.log('✅ Appointment limit check passed:', limitCheck);
          }
        } catch (error: any) {
          // Re-throw limit reached error
          if (error.message === 'APPOINTMENT_LIMIT_REACHED') {
            throw error;
          }
          // For other errors, log but continue (fail open)
          console.warn('⚠️ Appointment limit check failed, continuing anyway:', error);
        }
      }

      console.log('🔵 AppointmentManager: Getting existing appointments...');
      const appointments = await this.getAppointments();
      console.log(`🔵 AppointmentManager: Found ${appointments.length} existing appointments`);
      
      const newAppointment: Appointment = {
        ...appointment,
        // Backward-compatible default for multi-location businesses
        locationId: (appointment as any).locationId ?? ((appointment as any).businessOwnerId ? 'primary' : undefined),
        id: `apt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        createdAt: new Date().toISOString(),
        status: 'confirmed',
        queueStage: 'booked',
        estimatedDurationMinutes: (appointment as any).estimatedDurationMinutes ?? this.DEFAULT_SERVICE_DURATION_MINUTES,
        delayMinutes: (appointment as any).delayMinutes ?? 0,
      };
      
      console.log('🔵 AppointmentManager: Created new appointment:', {
        id: newAppointment.id,
        email: newAppointment.email,
        customerName: newAppointment.customerName
      });
      
      appointments.push(newAppointment);
      console.log(`🔵 AppointmentManager: Total appointments after adding: ${appointments.length}`);
      
      // Save to local storage (for offline access)
      console.log(`🔵 AppointmentManager: Saving to ProductionStorage with key: ${this.APPOINTMENTS_KEY}`);
      const appointmentsJson = JSON.stringify(appointments);
      console.log(`🔵 AppointmentManager: Appointments JSON length: ${appointmentsJson.length} characters`);
      await ProductionStorage.setItem(this.APPOINTMENTS_KEY, appointmentsJson);
      console.log(`✅ AppointmentManager: Appointments saved to ProductionStorage (key: bookapt_${this.APPOINTMENTS_KEY})`);
      
      // Also save to Firestore (for cross-browser/device access)
      if (Platform.OS === 'web') {
        try {
          // Use require() to ensure same module instance as unifiedAuthService
          const { doc, setDoc } = require('firebase/firestore');
          const { getWebDb, initializeFirebase } = require('../services/unifiedAuthService');
          
          // Ensure Firebase is initialized before using it
          const initialized = initializeFirebase();
          if (!initialized) {
            console.warn('⚠️  AppointmentManager: Firebase not initialized, skipping Firestore save');
          } else {
            const firestoreDb = getWebDb();
            if (!firestoreDb) {
              console.warn('⚠️  AppointmentManager: No Firebase database instance found, skipping Firestore save');
            } else {
              // Validate Firestore instance (same pattern as productManagementService)
              if (typeof firestoreDb !== 'object' || firestoreDb === null) {
                console.warn('⚠️  AppointmentManager: Invalid Firestore instance, skipping Firestore save');
              } else {
                // Remove undefined values from appointment (Firestore doesn't allow undefined)
                const removeUndefined = (obj: any): any => {
                  if (obj === null || obj === undefined) {
                    return null;
                  }
                  if (Array.isArray(obj)) {
                    return obj.map(removeUndefined).filter(item => item !== undefined);
                  }
                  if (typeof obj === 'object') {
                    const cleaned: any = {};
                    Object.keys(obj).forEach(key => {
                      const value = obj[key];
                      if (value !== undefined) {
                        cleaned[key] = removeUndefined(value);
                      }
                    });
                    return cleaned;
                  }
                  return obj;
                };

                const cleanAppointment = removeUndefined(newAppointment);

                const appointmentDocRef = doc(firestoreDb, 'appointments', newAppointment.id);
                await setDoc(appointmentDocRef, {
                  ...cleanAppointment,
                  updatedAt: new Date().toISOString(),
                });
                console.log(`✅ AppointmentManager: Appointment saved to Firestore: ${newAppointment.id}`);
                console.log(`✅ AppointmentManager: Appointment email: ${newAppointment.email}, customerName: ${newAppointment.customerName}`);
              }
            }
          }
        } catch (firestoreError: any) {
          console.error('❌ AppointmentManager: Error saving appointment to Firestore:', firestoreError);
          console.error('❌ Error message:', firestoreError?.message);
          console.error('❌ Error code:', firestoreError?.code);
          console.warn('⚠️  Appointment saved locally but not in Firestore');
          // Don't throw - local save succeeded
        }
      }
      
      // Send notification to admin
      await this.sendAdminNotification(newAppointment);
      
      // Dispatch event for real-time updates (web)
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        try {
          // Dispatch custom event
          window.dispatchEvent(new CustomEvent('newAppointment', {
            detail: { appointment: newAppointment }
          }));
          
          // Also set localStorage marker for polling
          localStorage.setItem(`new_appointment_${newAppointment.id}`, Date.now().toString());
          console.log('📬 AppointmentManager: Dispatched newAppointment event');
        } catch (error) {
          console.error('Error dispatching new appointment event:', error);
        }
      }
      
      return newAppointment;
    } catch (error) {
      console.error('Error saving appointment:', error);
      throw error;
    }
  }

  // Get all appointments (Firestore is source of truth; demo appointments are seeded via scripts/seed-demo-appointments.js)
  async getAppointments(): Promise<Appointment[]> {
    try {
      let loadedAppointments: Appointment[] = [];
      let loadedFromFirestore = false;
      const previousLocal = await this.readLocalAppointmentsSnapshot();

      if (Platform.OS === 'web') {
        try {
          // Use require() instead of import() to ensure same module instance as unifiedAuthService
          // This is critical: require() and import() create different module instances in webpack
          const { collection, getDocs } = require('firebase/firestore');
          const { getWebDb, initializeFirebase } = require('../services/unifiedAuthService');
          
          // Ensure Firebase is initialized before using it
          const initialized = initializeFirebase();
          if (!initialized) {
            console.warn('⚠️  AppointmentManager: Firebase not initialized, skipping Firestore load');
          } else {
            const firestoreDb = getWebDb();
            if (!firestoreDb) {
              console.warn('⚠️  AppointmentManager: No Firebase database instance found, skipping Firestore load');
            } else {
              // Validate Firestore instance (same pattern as productManagementService)
              if (typeof firestoreDb !== 'object' || firestoreDb === null) {
                console.warn('⚠️  AppointmentManager: Invalid Firestore instance, skipping Firestore load');
              } else {
                try {
                  // Use collection() with the db instance from unifiedAuthService
                  // Using require() ensures we use the same Firebase module instance
                  const appointmentsCollection = collection(firestoreDb, 'appointments');
                  const appointmentsSnapshot = await getDocs(appointmentsCollection);
                  loadedAppointments = appointmentsSnapshot.docs.map((doc: any) => ({
                    id: doc.id,
                    ...doc.data()
                  } as Appointment));
                  loadedFromFirestore = true;
                  loadedAppointments = this.mergeFirestoreAppointmentsWithLocal(
                    loadedAppointments,
                    previousLocal
                  );
                  console.log(`✅ AppointmentManager: Loaded ${loadedAppointments.length} appointments from Firestore`);
                  
                  // Log sample appointment data for debugging
                  if (loadedAppointments.length > 0) {
                    console.log('🔍 Sample appointment from Firestore:', {
                      id: loadedAppointments[0].id,
                      email: loadedAppointments[0].email,
                      customerName: loadedAppointments[0].customerName,
                      serviceType: loadedAppointments[0].serviceType,
                      appointmentDate: loadedAppointments[0].appointmentDate
                    });
                  }
                  
                  // Sync to local storage for offline access
                  await ProductionStorage.setItem(this.APPOINTMENTS_KEY, JSON.stringify(loadedAppointments));
                } catch (collectionError: any) {
                  console.error('❌ AppointmentManager: Error with Firestore collection:', collectionError);
                  console.error('❌ Error message:', collectionError?.message);
                  console.error('❌ Error code:', collectionError?.code);
                  // Re-throw to be caught by outer catch block
                  throw collectionError;
                }
              }
            }
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  AppointmentManager: Could not load appointments from Firestore, trying local storage:', firestoreError);
          console.warn('⚠️  Error message:', firestoreError?.message);
          console.warn('⚠️  Error code:', firestoreError?.code);
        }
      } else {
        // Mobile: load from Firestore (same as web; demo appointments are in Firestore after running scripts/seed-demo-appointments.js)
        try {
          const { initializeFirebase } = require('../services/unifiedAuthService');
          const initialized = initializeFirebase();
          if (initialized) {
            const { getApp } = require('@react-native-firebase/app');
            const { getFirestore, collection, getDocs } = require('@react-native-firebase/firestore');
            const app = getApp();
            const db = getFirestore(app);
      const appointmentsRef = collection(db, 'appointments');
      const snapshot = await getDocs(appointmentsRef);
      loadedAppointments = snapshot.docs.map((d: any) => ({
              id: d.id,
              ...d.data()
            } as Appointment));
            loadedFromFirestore = true;
            loadedAppointments = this.mergeFirestoreAppointmentsWithLocal(
              loadedAppointments,
              previousLocal
            );
            console.log(`✅ AppointmentManager: Loaded ${loadedAppointments.length} appointments from Firestore (mobile)`);
            await ProductionStorage.setItem(this.APPOINTMENTS_KEY, JSON.stringify(loadedAppointments));
          }
        } catch (mobileFirestoreError: any) {
          console.warn('⚠️  AppointmentManager: Mobile Firestore load failed, using local storage:', mobileFirestoreError?.message);
        }
      }

      // Fallback to local storage if Firestore didn't load anything
      if (!loadedFromFirestore) {
        const appointmentsJson = await ProductionStorage.getItem(this.APPOINTMENTS_KEY);
        if (appointmentsJson) {
          try {
            loadedAppointments = JSON.parse(appointmentsJson);
            console.log(`📋 AppointmentManager: Loaded ${loadedAppointments.length} appointments from local storage`);
          } catch (_) {
            loadedAppointments = [];
          }
        } else {
          loadedAppointments = loadedAppointments || [];
        }
      }

      return loadedAppointments;
    } catch (error) {
      console.error('Error getting appointments:', error);
      return [];
    }
  }

  // Get all appointments synchronously (for UI)
  getAppointmentsSync(): Appointment[] {
    try {
      const appointmentsJson = ProductionStorage.getItemSync(this.APPOINTMENTS_KEY);
      return appointmentsJson ? JSON.parse(appointmentsJson) : [];
    } catch (error) {
      console.error('Error getting appointments sync:', error);
      return [];
    }
  }

  /**
   * Get appointments for a business in a date range (fast load - no full collection read).
   * Requires Firestore composite index: businessOwnerId (ASC), appointmentDate (ASC).
   */
  async getAppointmentsByDateRange(
    businessOwnerId: string,
    startDate: string,
    endDate: string,
    options?: { limit?: number; startAfterDoc?: any }
  ): Promise<Appointment[]> {
    const limit = options?.limit ?? 200;
    try {
      if (Platform.OS === 'web') {
        const { collection, getDocs, query, where, orderBy, limit: limitFn, startAfter } = require('firebase/firestore');
        const { getWebDb, initializeFirebase } = require('../services/unifiedAuthService');
        if (!initializeFirebase() || !getWebDb()) return [];
        const db = getWebDb();
        const col = collection(db, 'appointments');
        let q = query(
          col,
          where('businessOwnerId', '==', businessOwnerId),
          where('appointmentDate', '>=', startDate),
          where('appointmentDate', '<=', endDate),
          orderBy('appointmentDate', 'asc'),
          limitFn(limit)
        );
        if (options?.startAfterDoc) {
          q = query(
            col,
            where('businessOwnerId', '==', businessOwnerId),
            where('appointmentDate', '>=', startDate),
            where('appointmentDate', '<=', endDate),
            orderBy('appointmentDate', 'asc'),
            startAfter(options.startAfterDoc),
            limitFn(limit)
          );
        }
        const snap = await getDocs(q);
        const list = snap.docs.map((d: any) => ({ id: d.id, ...d.data() } as Appointment));
        return list;
      }
      const { getApp } = require('@react-native-firebase/app');
      const { getFirestore, collection, getDocs, query, where, orderBy, limit: limitFn, startAfter } = require('@react-native-firebase/firestore');
      const app = getApp();
      const db = getFirestore(app);
      const col = collection(db, 'appointments');
      let q = query(
        col,
        where('businessOwnerId', '==', businessOwnerId),
        where('appointmentDate', '>=', startDate),
        where('appointmentDate', '<=', endDate),
        orderBy('appointmentDate', 'asc'),
        limitFn(limit)
      );
      if (options?.startAfterDoc) {
        q = query(
          col,
          where('businessOwnerId', '==', businessOwnerId),
          where('appointmentDate', '>=', startDate),
          where('appointmentDate', '<=', endDate),
          orderBy('appointmentDate', 'asc'),
          startAfter(options.startAfterDoc),
          limitFn(limit)
        );
      }
      const snap = await getDocs(q);
    return snap.docs.map((d: any) => ({ id: d.id, ...d.data() } as Appointment));
    } catch (error) {
      console.warn('getAppointmentsByDateRange failed, falling back to getAppointments filter:', error);
      const all = await this.getAppointments();
      return all
        .filter(a => (a as any).businessOwnerId === businessOwnerId && a.appointmentDate >= startDate && a.appointmentDate <= endDate)
        .sort((a, b) => a.appointmentDate.localeCompare(b.appointmentDate) || (a.appointmentTime || '').localeCompare(b.appointmentTime || ''))
        .slice(0, limit);
    }
  }

  // Update appointment status
  async updateAppointmentStatus(appointmentId: string, status: Appointment['status']): Promise<void> {
    try {
      const appointments = await this.getAppointments();
      const appointmentIndex = appointments.findIndex(apt => apt.id === appointmentId);
      
      if (appointmentIndex === -1) {
        throw new Error(`Appointment ${appointmentId} not found`);
      }
      
      appointments[appointmentIndex].status = status;
      if (status === 'completed') {
        appointments[appointmentIndex].queueStage = 'completed';
        appointments[appointmentIndex].serviceCompletedAt = new Date().toISOString();
      }
      if (status === 'cancelled') {
        appointments[appointmentIndex].queueStage = 'no_show';
      }
      const updatedAppointment = appointments[appointmentIndex];
      
      // Save to local storage (for offline access)
      await ProductionStorage.setItem(this.APPOINTMENTS_KEY, JSON.stringify(appointments));
      
      // Dispatch event for auto-refresh
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('appointmentUpdated', {
          detail: { appointmentId, status }
        }));
        // Set localStorage marker
        localStorage.setItem(`appointment_update_${appointmentId}`, Date.now().toString());
      }
      
      await this.persistAppointmentPatchToTopLevelFirestore(appointmentId, {
        status,
        queueStage: updatedAppointment.queueStage,
        serviceCompletedAt: updatedAppointment.serviceCompletedAt,
      });
      console.log(`✅ AppointmentManager: Appointment status merged to appointments/${appointmentId}`);
      
      // Send status update notification (don't fail if this fails)
      try {
        await this.sendStatusUpdateNotification(updatedAppointment);
      } catch (notifError: any) {
        console.warn('⚠️  AppointmentManager: Failed to send status update notification:', notifError);
        // Don't throw - appointment update succeeded
      }
    } catch (error: any) {
      console.error('❌ AppointmentManager: Error updating appointment status:', error);
      throw error;
    }
  }

  /** Permanently delete an appointment from Firestore and local storage. */
  async deleteAppointment(appointmentId: string): Promise<void> {
    try {
      const appointments = await this.getAppointments();
      const appointmentIndex = appointments.findIndex(apt => apt.id === appointmentId);

      if (appointmentIndex === -1) {
        throw new Error(`Appointment ${appointmentId} not found`);
      }

      await this.deleteAppointmentFromTopLevelFirestore(appointmentId);

      appointments.splice(appointmentIndex, 1);
      await ProductionStorage.setItem(this.APPOINTMENTS_KEY, JSON.stringify(appointments));

      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('appointmentDeleted', {
          detail: { appointmentId },
        }));
        localStorage.setItem(`appointment_deleted_${appointmentId}`, Date.now().toString());
      }

      console.log(`✅ AppointmentManager: Appointment deleted: ${appointmentId}`);
    } catch (error: any) {
      console.error('❌ AppointmentManager: Error deleting appointment:', error);
      throw error;
    }
  }

  async updateAppointmentQueueFields(
    appointmentId: string,
    updates: Partial<Pick<
      Appointment,
      | 'queueStage'
      | 'checkedInAt'
      | 'serviceStartedAt'
      | 'serviceCompletedAt'
      | 'estimatedDurationMinutes'
      | 'delayMinutes'
      | 'delayReason'
      | 'workerId'
      | 'workerName'
    >>
  ): Promise<void> {
    const appointments = await this.getAppointments();
    const appointmentIndex = appointments.findIndex(apt => apt.id === appointmentId);
    if (appointmentIndex === -1) {
      throw new Error(`Appointment ${appointmentId} not found`);
    }

    const next = {
      ...appointments[appointmentIndex],
      ...updates,
    };
    appointments[appointmentIndex] = next;

    await ProductionStorage.setItem(this.APPOINTMENTS_KEY, JSON.stringify(appointments));

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      try {
        window.dispatchEvent(new CustomEvent('appointmentUpdated', {
          detail: { appointmentId, updates, at: new Date().toISOString() }
        }));
        localStorage.setItem(`appointment_update_${appointmentId}`, Date.now().toString());
      } catch (e) {
        console.warn('Could not dispatch appointmentUpdated after queue update:', e);
      }
    }

    await this.persistAppointmentPatchToTopLevelFirestore(appointmentId, {
      queueStage: next.queueStage,
      checkedInAt: next.checkedInAt,
      serviceStartedAt: next.serviceStartedAt,
      serviceCompletedAt: next.serviceCompletedAt,
      estimatedDurationMinutes: next.estimatedDurationMinutes,
      delayMinutes: next.delayMinutes,
      delayReason: next.delayReason,
      workerId: next.workerId,
      workerName: next.workerName,
    });

    // Reuse status notification pipeline so queue changes surface to business dashboards/feeds.
    try {
      await this.sendStatusUpdateNotification({
        ...next,
        status: next.status,
      });
    } catch (notifError) {
      console.warn('Queue field notification failed:', notifError);
    }
  }

  computeQueueInsights(appointments: Appointment[], options?: { businessOwnerId?: string; locationId?: string; workerId?: string }): Record<string, QueueInsight> {
    const now = new Date();
    const activeStatuses = new Set(['confirmed', 'pending', 'pending_business_confirmation', 'pending_reschedule', 'business_will_call']);

    const scoped = (appointments || [])
      .filter((apt) => !!apt?.appointmentDate && !!apt?.appointmentTime)
      .filter((apt) => !options?.businessOwnerId || apt.businessOwnerId === options.businessOwnerId)
      .filter((apt) => !options?.locationId || apt.locationId === options.locationId)
      .filter((apt) => !options?.workerId || apt.workerId === options.workerId)
      .filter((apt) => activeStatuses.has(apt.status) || apt.queueStage === 'in_service')
      .sort((a, b) => a.appointmentDate.localeCompare(b.appointmentDate) || (a.appointmentTime || '').localeCompare(b.appointmentTime || '') || (a.createdAt || '').localeCompare(b.createdAt || ''));

    const insights: Record<string, QueueInsight> = {};
    let pointer = new Date(now);
    let servingNumber: number | null = null;

    scoped.forEach((apt, idx) => {
      const number = idx + 1;
      if (apt.queueStage === 'in_service' && servingNumber === null) {
        servingNumber = number;
      }
    });

    scoped.forEach((apt, idx) => {
      const number = idx + 1;
      const plannedStart = this.combineDateAndTime(apt.appointmentDate, apt.appointmentTime);
      const baseDuration = Math.max(10, apt.estimatedDurationMinutes ?? this.DEFAULT_SERVICE_DURATION_MINUTES);
      const delay = Math.max(0, apt.delayMinutes ?? 0);
      const serviceMinutes = baseDuration + delay;

      if (plannedStart && plannedStart > pointer) pointer = plannedStart;
      const estimatedStart = new Date(pointer);
      const estimatedEnd = new Date(estimatedStart.getTime() + serviceMinutes * 60000);

      const peopleAhead = Math.max(0, idx - (servingNumber ? servingNumber - 1 : 0));
      const variance = this.getEtaVarianceMinutes(apt);

      insights[apt.id] = {
        appointmentId: apt.id,
        yourNumber: number,
        currentServingNumber: servingNumber,
        peopleAhead,
        estimatedStartAt: estimatedStart.toISOString(),
        estimatedWindowStartAt: new Date(estimatedStart.getTime() - variance * 60000).toISOString(),
        estimatedWindowEndAt: new Date(estimatedStart.getTime() + variance * 60000).toISOString(),
        confidence: this.getConfidence(apt, variance),
        lastUpdatedAt: now.toISOString(),
      };

      pointer = estimatedEnd;
    });

    return insights;
  }

  private combineDateAndTime(date: string, time: string): Date | null {
    try {
      if (!date || !time) return null;
      const cleanTime = time.trim().toUpperCase();
      const amPmMatch = cleanTime.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
      if (amPmMatch) {
        let hour = parseInt(amPmMatch[1], 10);
        const min = parseInt(amPmMatch[2], 10);
        const suffix = amPmMatch[3];
        if (suffix === 'PM' && hour < 12) hour += 12;
        if (suffix === 'AM' && hour === 12) hour = 0;
        const [y, m, d] = date.split('-').map((x) => parseInt(x, 10));
        return new Date(y, (m || 1) - 1, d || 1, hour, min, 0, 0);
      }
      const [hourStr, minStr] = cleanTime.split(':');
      const hour = parseInt(hourStr, 10);
      const min = parseInt(minStr, 10);
      const [y, m, d] = date.split('-').map((x) => parseInt(x, 10));
      return new Date(y, (m || 1) - 1, d || 1, isNaN(hour) ? 0 : hour, isNaN(min) ? 0 : min, 0, 0);
    } catch {
      return null;
    }
  }

  private getEtaVarianceMinutes(apt: Appointment): number {
    if ((apt.delayMinutes ?? 0) >= 20) return 20;
    if ((apt.delayMinutes ?? 0) >= 10) return 15;
    if (apt.queueStage === 'in_service') return 8;
    return 10;
  }

  private getConfidence(apt: Appointment, variance: number): 'high' | 'medium' | 'low' {
    if (variance <= 10 && (apt.delayMinutes ?? 0) <= 5) return 'high';
    if (variance <= 15) return 'medium';
    return 'low';
  }

  // Send notification to admin about new appointment
  private async sendAdminNotification(appointment: Appointment): Promise<void> {
    try {
      // 1. Create in-app notification
      const notifications = await this.getNotifications();
      const notification = {
        id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: 'new_appointment',
        title: 'New Appointment Booked!',
        message: `${appointment.customerName} booked ${appointment.serviceType} for ${appointment.appointmentTime}`,
        appointmentId: appointment.id,
        timestamp: new Date().toISOString(),
        read: false
      };
      
      notifications.unshift(notification);
      
      // Save to local storage (for offline access)
      await ProductionStorage.setItem(this.NOTIFICATIONS_KEY, JSON.stringify(notifications));
      
      // Also save to Firestore (for cross-browser/device access)
      if (Platform.OS === 'web' && appointment.businessOwnerId) {
        try {
          const { doc, setDoc, collection } = await import('firebase/firestore');
          const { getWebDb, initializeFirebase } = await import('../services/unifiedAuthService');
          
          // Ensure Firebase is initialized before using it
          const initialized = initializeFirebase();
          if (!initialized) {
            console.warn('⚠️  AppointmentManager: Firebase not initialized, skipping Firestore save');
          } else {
            const db = getWebDb();
            if (db) {
              const businessDocRef = doc(db, 'businesses', appointment.businessOwnerId);
              const notificationsCollectionRef = collection(businessDocRef, 'notifications');
              await setDoc(doc(notificationsCollectionRef, notification.id), {
                ...notification,
                updatedAt: new Date().toISOString(),
              });
              console.log(`✅ AppointmentManager: Notification saved to Firestore: ${notification.id}`);
            } else {
              console.warn('⚠️  AppointmentManager: No Firebase database instance found, skipping Firestore save');
            }
          }
        } catch (firestoreError: any) {
          console.error('❌ AppointmentManager: Error saving notification to Firestore:', firestoreError);
          // Don't throw - local save succeeded
        }
      }
      
      console.log('📱 In-app notification sent:', notification);

      // 2. Send push notification to business owner (if businessOwnerId exists)
      if (appointment.businessOwnerId) {
        try {
          const { pushNotificationService } = await import('../services/pushNotificationService');
          await pushNotificationService.sendNewBookingNotification(
            appointment.businessOwnerId,
            {
              customerName: appointment.customerName,
              service: appointment.serviceType,
              date: appointment.appointmentDate,
              time: appointment.appointmentTime,
              appointmentId: appointment.id
            }
          );
          console.log('📱 Push notification sent to business owner');
        } catch (error) {
          console.error('Error sending push notification to business owner:', error);
          // Don't throw - in-app notification is more important
        }
      }
    } catch (error) {
      console.error('Error sending admin notification:', error);
    }
  }

  // Send notification about status update
  private async sendStatusUpdateNotification(appointment: Appointment): Promise<void> {
    try {
      const notifications = await this.getNotifications();
      const notification = {
        id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: 'status_update',
        title: 'Appointment Status Updated',
        message: `${appointment.customerName}'s appointment status changed to ${appointment.status}`,
        appointmentId: appointment.id,
        timestamp: new Date().toISOString(),
        read: false
      };
      
      notifications.unshift(notification);
      
      // Save to local storage (for offline access)
      await ProductionStorage.setItem(this.NOTIFICATIONS_KEY, JSON.stringify(notifications));
      
      // Also save to Firestore (for cross-browser/device access)
      if (Platform.OS === 'web' && appointment.businessOwnerId) {
        try {
          const { doc, setDoc } = await import('firebase/firestore');
          const { getWebDb } = await import('../services/unifiedAuthService');
          const db = getWebDb();
          
          if (db) {
            await setDoc(doc(db, 'businesses', appointment.businessOwnerId, 'notifications', notification.id), {
              ...notification,
              updatedAt: new Date().toISOString(),
            });
            console.log(`✅ AppointmentManager: Status notification saved to Firestore: ${notification.id}`);
          }
        } catch (firestoreError: any) {
          console.error('❌ AppointmentManager: Error saving status notification to Firestore:', firestoreError);
          // Don't throw - local save succeeded
        }
      }
      
      console.log('📱 Status update notification sent:', notification);
    } catch (error) {
      console.error('Error sending status update notification:', error);
    }
  }

  // Get all notifications
  async getNotifications(): Promise<any[]> {
    try {
      // Try to load from Firestore first (most up-to-date)
      let loadedNotifications: any[] = [];
      let loadedFromFirestore = false;
      
      if (Platform.OS === 'web') {
        try {
          const { collection, query, getDocs, orderBy, doc } = await import('firebase/firestore');
          const { getWebDb, initializeFirebase } = await import('../services/unifiedAuthService');
          const { AuthManager } = await import('./authManager');
          
          // Ensure Firebase is initialized before using it
          const initialized = initializeFirebase();
          if (!initialized) {
            console.warn('⚠️  AppointmentManager: Firebase not initialized, skipping Firestore load');
          } else {
            const db = getWebDb();
            if (db) {
              // Get current user to filter notifications
              const user = await AuthManager.getSavedUser();
              const businessOwner = await AuthManager.getSavedBusinessOwner();
              const userId = user?.id || businessOwner?.id;
              
              if (userId) {
                // Try to get user-specific notifications
                const userDocRef = doc(db, 'users', userId);
                const userNotificationsCollectionRef = collection(userDocRef, 'notifications');
                const userNotificationsQuery = query(
                  userNotificationsCollectionRef,
                  orderBy('timestamp', 'desc')
                );
                const userNotificationsSnapshot = await getDocs(userNotificationsQuery);
                loadedNotifications = userNotificationsSnapshot.docs.map((docSnap: any) => ({
                  id: docSnap.id,
                  ...docSnap.data()
                }));
                
                // Also get business owner notifications if applicable
                if (businessOwner) {
                  const businessDocRef = doc(db, 'businesses', businessOwner.id);
                  const businessNotificationsCollectionRef = collection(businessDocRef, 'notifications');
                  const businessNotificationsQuery = query(
                    businessNotificationsCollectionRef,
                    orderBy('timestamp', 'desc')
                  );
                  const businessNotificationsSnapshot = await getDocs(businessNotificationsQuery);
                  const businessNotifications = businessNotificationsSnapshot.docs.map((docSnap: any) => ({
                    id: docSnap.id,
                    ...docSnap.data()
                  }));
                  loadedNotifications = [...loadedNotifications, ...businessNotifications];
                }
                
                loadedFromFirestore = true;
                console.log(`✅ AppointmentManager: Loaded ${loadedNotifications.length} notifications from Firestore`);
                
                // Sync to local storage for offline access
                await ProductionStorage.setItem(this.NOTIFICATIONS_KEY, JSON.stringify(loadedNotifications));
              }
            } else {
              console.warn('⚠️  AppointmentManager: No Firebase database instance found, skipping Firestore load');
            }
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  AppointmentManager: Could not load notifications from Firestore, trying local storage:', firestoreError);
        }
      }
      
      // Fallback to local storage if Firestore didn't work
      if (!loadedFromFirestore) {
      const notificationsJson = await ProductionStorage.getItem(this.NOTIFICATIONS_KEY);
        if (notificationsJson) {
          loadedNotifications = JSON.parse(notificationsJson);
          console.log(`📋 AppointmentManager: Loaded ${loadedNotifications.length} notifications from local storage`);
        }
      }
      
      return loadedNotifications;
    } catch (error) {
      console.error('Error getting notifications:', error);
      return [];
    }
  }

  // Mark notification as read
  async markNotificationAsRead(notificationId: string): Promise<void> {
    try {
      const notifications = await this.getNotifications();
      const notificationIndex = notifications.findIndex(notif => notif.id === notificationId);
      
      if (notificationIndex !== -1) {
        notifications[notificationIndex].read = true;
        // Save to local storage (for offline access)
        await ProductionStorage.setItem(this.NOTIFICATIONS_KEY, JSON.stringify(notifications));
        
        // Also update in Firestore (for cross-browser/device access)
        if (Platform.OS === 'web') {
          try {
            const { doc, updateDoc } = await import('firebase/firestore');
            const { getWebDb } = await import('../services/unifiedAuthService');
            const { AuthManager } = await import('./authManager');
            const db = getWebDb();
            
            if (db) {
              const businessOwner = await AuthManager.getSavedBusinessOwner();
              const user = await AuthManager.getSavedUser();
              const userId = businessOwner?.id || user?.id;
              
              if (userId) {
                // Try business notifications first
                if (businessOwner) {
                  try {
                    await updateDoc(doc(db, 'businesses', businessOwner.id, 'notifications', notificationId), {
                      read: true,
                      updatedAt: new Date().toISOString(),
                    });
                    console.log(`✅ AppointmentManager: Notification marked as read in Firestore: ${notificationId}`);
                    return;
                  } catch (e) {
                    // Try user notifications if business notification doesn't exist
                  }
                }
                
                // Try user notifications
                await updateDoc(doc(db, 'users', userId, 'notifications', notificationId), {
                  read: true,
                  updatedAt: new Date().toISOString(),
                });
                console.log(`✅ AppointmentManager: Notification marked as read in Firestore: ${notificationId}`);
              }
            }
          } catch (firestoreError: any) {
            console.error('❌ AppointmentManager: Error updating notification in Firestore:', firestoreError);
            // Don't throw - local update succeeded
          }
        }
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }

  // Get unread notification count
  async getUnreadNotificationCount(): Promise<number> {
    try {
      const notifications = await this.getNotifications();
      return notifications.filter(notif => !notif.read).length;
    } catch (error) {
      console.error('Error getting unread notification count:', error);
      return 0;
    }
  }
}

export const appointmentManager = new AppointmentManager();
